import { Grid } from "@mui/material";
import { ASSET_DETAILS_PANEL } from "@/constants";
import { GlassesGuideDetailsProps } from "@/utils/interface";
import {
  Asset,
  GLASSES_NVDB_SEMANTIC_KEYS,
  GlassesGuideNvdbModel,
  LoanDetails,
} from "@/store/slices/types/applicationFormSlice";
import { cleanNumber, formatCurrencyValue, formatRangeLowToHigh, toFiniteNumber } from "@/utils/formatNumber";
import { hasValue } from "@/utils/commonFunctions";
import AssetDetailsLayout, { InfoRow } from "./AssetDetailsLayout";

/**
 * Optional fields that only exist on some ModelTypeCode responses (engine/transmission on A,
 * tyres on B, berth/axle on C, etc.)
 */
const DYNAMIC_FIELDS: { key: string; label: string; suffix?: string }[] = [
  { key: "EngineName", label: "Engine" },
  { key: "CCName", label: "Engine Capacity" },
  { key: "KW", label: "Power", suffix: " kW" },
  { key: "AVERAGEKM", label: "Average KM", suffix: " km" },
  { key: "ANCAP", label: "ANCAP Rating" },
];

/** Only a Glass's payload carries a class code; AutoGrab's has its own panel. */
const isGlassesSpecification = (specifications?: Asset["specifications"]): specifications is GlassesGuideNvdbModel =>
  Boolean(specifications && specifications?.ClassCode);

const SpecificationsSection = ({
  specifications,
  asset,
  loanDetails,
}: {
  specifications: GlassesGuideNvdbModel;
  asset: Asset;
  loanDetails: LoanDetails;
}) => {
  const modelType = specifications.ModelTypeCode;

  // Some fields share a meaning but use different keys per ModelTypeCode (e.g. KerbWeight vs Weight).
  const getSemantic = (semantic: keyof typeof GLASSES_NVDB_SEMANTIC_KEYS) =>
    specifications[GLASSES_NVDB_SEMANTIC_KEYS[semantic][modelType]];

  const serviceKms = getSemantic("serviceIntervalKms");
  const curbMass = getSemantic("curbMassKg");

  const retail = toFiniteNumber(specifications?.Retail);
  const amountFinanced =
    (asset?.assetValue || 0) * 1 -
    (loanDetails?.deposit || 0) * 1 -
    (loanDetails?.tradeIn || 0) * 1 +
    (loanDetails?.payout || 0) * 1;
  const estimatedLVR = retail > 0 ? ((amountFinanced / retail) * 100 || 0).toFixed(2) : null;

  return (
    <Grid container item xs={12}>
      {/* Valuation — common across all types */}
      {estimatedLVR && <InfoRow label="Estimated LVR:" value={`${estimatedLVR}%`} />}
      <InfoRow label="RRP:" value={formatCurrencyValue(specifications?.RRP)} />
      <InfoRow label="Retail:" value={formatCurrencyValue(specifications?.Retail)} />
      <InfoRow label="Trade:" value={formatRangeLowToHigh(specifications?.TradeLow, specifications?.Trade)} />

      {/* Cross-type fields — same meaning, different key per ModelTypeCode */}
      {hasValue(curbMass) && <InfoRow label="Kerb / Tare Weight:" value={`${cleanNumber(curbMass)} kg`} />}
      {hasValue(serviceKms) && <InfoRow label="Service Interval (km):" value={`${cleanNumber(serviceKms)} km`} />}

      {/* Dynamic fields — rendered only when the response carries them */}
      {DYNAMIC_FIELDS.filter(({ key }) => hasValue(specifications?.[key])).map(({ key, label, suffix }) => (
        <InfoRow key={key} label={`${label}:`} value={`${cleanNumber(specifications?.[key])}${suffix || ""}`} />
      ))}
    </Grid>
  );
};

const GlassesGuideDetails = ({ asset, loanDetails }: GlassesGuideDetailsProps) => {
  const specifications = asset?.specifications;

  return (
    <AssetDetailsLayout
      asset={asset}
      heading={ASSET_DETAILS_PANEL.GLASSES_HEADING}
      specifications={
        isGlassesSpecification(specifications) ? (
          <SpecificationsSection specifications={specifications} asset={asset} loanDetails={loanDetails} />
        ) : null
      }
    />
  );
};

export default GlassesGuideDetails;
